/**
 * Host Global - Staff Profile Page
 * Displays and allows editing of staff member profile information
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Icons
import Icon from "@mui/material/Icon";

// API
import { userAPI, propertyOwnerAPI } from "utils/api";
import { useAuth } from "context/authContext";

function Profile() {
  const { user } = useAuth();
  const isPropertyOwner = user?.role === "Property_Owner" || user?.role === "Property Owner";
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    idNumber: "",
    phone: "",
    companyNumber: "",
    relevantPhoneNumbers: ["", ""],
    address: "",
    maritalStatus: "Single",
    familyMembersNumber: 0,
    personalEmail: "",
    nationality: "",
    gender: "Male",
    photo: null,
    idPhoto: null,
    // Property Owner specific fields
    personalPhoto: null,
    idCard: null,
    jobType: "",
    bankDetails: {
      bankName: "",
      accountHolderName: "",
      accountNumber: "",
      iban: "",
      swiftCode: "",
      branchName: "",
    },
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [idPhotoFile, setIdPhotoFile] = useState(null);
  const [personalPhotoFile, setPersonalPhotoFile] = useState(null);
  const [idCardFile, setIdCardFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [idPhotoPreview, setIdPhotoPreview] = useState(null);
  const [personalPhotoPreview, setPersonalPhotoPreview] = useState(null);
  const [idCardPreview, setIdCardPreview] = useState(null);
  const [bankErrors, setBankErrors] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      if (isPropertyOwner) {
        const response = await propertyOwnerAPI.getProfile();
        if (response.owner) {
          setProfileData({
            fullName: response.owner.fullName || "",
            email: response.owner.email || "",
            phone: response.owner.phone || "",
            address: response.owner.address || "",
            nationality: response.owner.nationality || "",
            jobType: response.owner.jobType || "",
            gender: response.owner.gender || "Male",
            personalPhoto: response.owner.personalPhoto,
            idCard: response.owner.idCard,
            bankDetails: response.owner.bankDetails || {
              bankName: "",
              accountHolderName: "",
              accountNumber: "",
              iban: "",
              swiftCode: "",
              branchName: "",
            },
          });
          const personalPhotoUrl =
            typeof response.owner.personalPhoto === "string"
              ? response.owner.personalPhoto
              : response.owner.personalPhoto?.url;
          const idCardUrl =
            typeof response.owner.idCard === "string"
              ? response.owner.idCard
              : response.owner.idCard?.url;
          setPersonalPhotoPreview(personalPhotoUrl || null);
          setIdCardPreview(idCardUrl || null);
        }
      } else {
        const response = await userAPI.getProfile();
        if (response.user) {
          setProfileData({
            fullName: response.user.fullName || "",
            email: response.user.email || "",
            idNumber: response.user.idNumber || "",
            phone: response.user.phone || "",
            companyNumber: response.user.companyNumber || "",
            relevantPhoneNumbers: response.user.relevantPhoneNumbers || ["", ""],
            address: response.user.address || "",
            maritalStatus: response.user.maritalStatus || "Single",
            familyMembersNumber: response.user.familyMembersNumber || 0,
            personalEmail: response.user.personalEmail || "",
            nationality: response.user.nationality || "",
            gender: response.user.gender || "Male",
            photo: response.user.photo,
            idPhoto: response.user.idPhoto,
          });
          // Backend returns photo/idPhoto as URL strings (user.photo?.url)
          const photoUrl =
            typeof response.user.photo === "string"
              ? response.user.photo
              : response.user.photo?.url;
          const idPhotoUrl =
            typeof response.user.idPhoto === "string"
              ? response.user.idPhoto
              : response.user.idPhoto?.url;
          setPhotoPreview(photoUrl || null);
          setIdPhotoPreview(idPhotoUrl || null);
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setMessage({ type: "error", text: "Failed to load profile. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("relevantPhone")) {
      const index = parseInt(name.replace("relevantPhone", ""));
      const newPhones = [...profileData.relevantPhoneNumbers];
      newPhones[index] = value;
      setProfileData({ ...profileData, relevantPhoneNumbers: newPhones });
    } else if (name.startsWith("bank")) {
      // Handle bank details fields (e.g., "bankBankName" -> "bankName")
      const bankField = name.replace("bank", "");
      // Convert first letter to lowercase (e.g., "BankName" -> "bankName")
      const fieldName = bankField.charAt(0).toLowerCase() + bankField.slice(1);
      const updatedBankDetails = {
        ...(profileData.bankDetails || {}),
        [fieldName]: value,
      };
      setProfileData({
        ...profileData,
        bankDetails: updatedBankDetails,
      });

      // Real-time validation for bank fields
      const errors = { ...bankErrors };
      delete errors[fieldName]; // Clear previous error for this field

      // Validate the field as user types
      if (fieldName === "bankName") {
        if (value.trim().length > 0 && value.trim().length < 2) {
          errors.bankName = "Bank name must be at least 2 characters";
        } else {
          delete errors.bankName;
        }
      } else if (fieldName === "accountHolderName") {
        if (value.trim().length > 0 && value.trim().length < 2) {
          errors.accountHolderName = "Account holder name must be at least 2 characters";
        } else {
          delete errors.accountHolderName;
        }
      } else if (fieldName === "accountNumber") {
        if (value.trim().length > 0 && value.trim().length < 5) {
          errors.accountNumber = "Account number must be at least 5 characters";
        } else if (value.trim().length > 0 && !/^[A-Z0-9]+$/i.test(value.trim())) {
          errors.accountNumber = "Account number must contain only letters and numbers";
        } else {
          delete errors.accountNumber;
        }
      } else if (fieldName === "iban") {
        if (value.trim().length > 0) {
          const iban = value.trim().replace(/\s/g, "");
          if (iban.length < 15 || iban.length > 34) {
            errors.iban = "IBAN must be between 15 and 34 characters";
          } else if (!/^[A-Z0-9]+$/i.test(iban)) {
            errors.iban = "IBAN must contain only letters and numbers";
          } else {
            delete errors.iban;
          }
        } else {
          delete errors.iban;
        }
      } else if (fieldName === "swiftCode") {
        if (value.trim().length > 0) {
          const swiftCode = value.trim().toUpperCase();
          if (swiftCode.length < 8 || swiftCode.length > 11) {
            errors.swiftCode = "SWIFT code must be between 8 and 11 characters";
          } else if (!/^[A-Z0-9]+$/.test(swiftCode)) {
            errors.swiftCode = "SWIFT code must contain only letters and numbers";
          } else {
            delete errors.swiftCode;
          }
        } else {
          delete errors.swiftCode;
        }
      }

      setBankErrors(errors);
    } else {
      setProfileData({ ...profileData, [name]: value });
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({ type: "error", text: "Please select an image file for photo" });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: "error", text: "Photo size must be less than 5MB" });
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIdPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setMessage({ type: "error", text: "ID photo must be a PDF file" });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: "error", text: "ID photo size must be less than 10MB" });
        return;
      }
      setIdPhotoFile(file);
      // For PDF preview, we'll just show the filename
    }
  };

  const handlePersonalPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({ type: "error", text: "Please select an image file for personal photo" });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: "error", text: "Personal photo size must be less than 5MB" });
        return;
      }
      setPersonalPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPersonalPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIdCardChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setMessage({ type: "error", text: "ID card must be a PDF file" });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: "error", text: "ID card size must be less than 10MB" });
        return;
      }
      setIdCardFile(file);
      // For PDF preview, we'll just show the filename
    }
  };

  // Validate bank details
  const validateBankDetails = () => {
    const errors = {};
    const bankDetails = profileData.bankDetails || {};

    // Bank Name validation
    if (!bankDetails.bankName || bankDetails.bankName.trim().length < 2) {
      errors.bankName = "Bank name is required (minimum 2 characters)";
    }

    // Account Holder Name validation
    if (!bankDetails.accountHolderName || bankDetails.accountHolderName.trim().length < 2) {
      errors.accountHolderName = "Account holder name is required (minimum 2 characters)";
    }

    // Account Number validation
    if (!bankDetails.accountNumber || bankDetails.accountNumber.trim().length < 5) {
      errors.accountNumber = "Account number is required (minimum 5 characters)";
    } else if (!/^[A-Z0-9]+$/i.test(bankDetails.accountNumber.trim())) {
      errors.accountNumber = "Account number must contain only letters and numbers";
    }

    // IBAN validation (optional but if provided, must be valid)
    if (bankDetails.iban && bankDetails.iban.trim() !== "") {
      const iban = bankDetails.iban.trim().replace(/\s/g, ""); // Remove spaces
      if (iban.length < 15 || iban.length > 34) {
        errors.iban = "IBAN must be between 15 and 34 characters";
      } else if (!/^[A-Z0-9]+$/i.test(iban)) {
        errors.iban = "IBAN must contain only letters and numbers";
      }
    }

    // SWIFT Code validation (optional but if provided, must be valid)
    if (bankDetails.swiftCode && bankDetails.swiftCode.trim() !== "") {
      const swiftCode = bankDetails.swiftCode.trim().toUpperCase();
      if (swiftCode.length < 8 || swiftCode.length > 11) {
        errors.swiftCode = "SWIFT code must be between 8 and 11 characters";
      } else if (!/^[A-Z0-9]+$/.test(swiftCode)) {
        errors.swiftCode = "SWIFT code must contain only letters and numbers";
      }
    }

    setBankErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    setBankErrors({});

    try {
      if (isPropertyOwner) {
        // Validate bank details if provided
        if (
          profileData.bankDetails &&
          (profileData.bankDetails.bankName ||
            profileData.bankDetails.accountHolderName ||
            profileData.bankDetails.accountNumber ||
            profileData.bankDetails.iban ||
            profileData.bankDetails.swiftCode ||
            profileData.bankDetails.branchName)
        ) {
          if (!validateBankDetails()) {
            setMessage({
              type: "error",
              text: "Please correct the bank details errors before submitting.",
            });
            setSubmitting(false);
            return;
          }
        }

        // Property Owner profile update
        const formData = new FormData();

        if (profileData.jobType) {
          formData.append("jobType", profileData.jobType);
        }
        if (profileData.nationality) {
          formData.append("nationality", profileData.nationality);
        }
        if (profileData.address) {
          formData.append("address", profileData.address);
        }
        if (profileData.gender) {
          formData.append("gender", profileData.gender);
        }

        // Add bank details if provided
        if (profileData.bankDetails) {
          formData.append("bankDetails", JSON.stringify(profileData.bankDetails));
        }

        // Add personal photo if changed
        if (personalPhotoFile) {
          formData.append("personalPhoto", personalPhotoFile);
        }

        // Add ID card if changed
        if (idCardFile) {
          formData.append("idCard", idCardFile);
        }

        const response = await propertyOwnerAPI.updateProfile(formData);
        if (response.message) {
          setMessage({ type: "success", text: response.message });
          // Update previews if files were uploaded
          if (response.owner) {
            if (response.owner.personalPhoto)
              setPersonalPhotoPreview(response.owner.personalPhoto.url);
            if (response.owner.idCard) setIdCardPreview(response.owner.idCard.url);
          }
          // Clear file inputs
          setPersonalPhotoFile(null);
          setIdCardFile(null);
          // Refresh profile data
          await fetchProfile();
        }
      } else {
        // Staff User profile update
        const formData = new FormData();

        // Add all form fields
        formData.append(
          "data",
          JSON.stringify({
            fullName: profileData.fullName,
            phone: profileData.phone,
            companyNumber: profileData.companyNumber,
            relevantPhoneNumbers: profileData.relevantPhoneNumbers.filter((p) => p.trim() !== ""),
            address: profileData.address,
            maritalStatus: profileData.maritalStatus,
            familyMembersNumber: parseInt(profileData.familyMembersNumber) || 0,
            personalEmail: profileData.personalEmail,
            nationality: profileData.nationality,
            gender: profileData.gender,
          })
        );

        // Add photo if changed
        if (photoFile) {
          formData.append("photo", photoFile);
        }

        // Add ID photo if changed
        if (idPhotoFile) {
          formData.append("idPhoto", idPhotoFile);
        }

        const response = await userAPI.updateProfile(formData);

        if (response.message) {
          setMessage({
            type: "success",
            text: response.message || "Profile updated successfully!",
          });
          // Update previews if files were uploaded (backend returns URL strings)
          if (response.user) {
            const photoUrl =
              typeof response.user.photo === "string"
                ? response.user.photo
                : response.user.photo?.url;
            const idPhotoUrl =
              typeof response.user.idPhoto === "string"
                ? response.user.idPhoto
                : response.user.idPhoto?.url;
            if (photoUrl) setPhotoPreview(photoUrl);
            if (idPhotoUrl) setIdPhotoPreview(idPhotoUrl);
          }
          // Clear file inputs
          setPhotoFile(null);
          setIdPhotoFile(null);
          // Refresh profile data
          fetchProfile();
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to update profile. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (passwordMessage.text) setPasswordMessage({ type: "", text: "" });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: "", text: "" });
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordMessage({ type: "error", text: "Please fill in all password fields." });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({ type: "error", text: "New password must be at least 6 characters long." });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: "error", text: "New password and confirmation do not match." });
      return;
    }
    try {
      setPasswordSubmitting(true);
      const api = isPropertyOwner ? propertyOwnerAPI : userAPI;
      await api.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordMessage({ type: "success", text: "Password changed successfully." });
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      setPasswordMessage({
        type: "error",
        text: error.message || "Failed to change password. Please try again.",
      });
    } finally {
      setPasswordSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox py={3} px={{ xs: 2, sm: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={3}>
                  My Profile
                </MDTypography>

                {message.text && (
                  <MDBox mb={2}>
                    <Alert severity={message.type}>{message.text}</Alert>
                  </MDBox>
                )}

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Photo Section - Property Owner */}
                    {isPropertyOwner && (
                      <>
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2} sx={{ width: { xs: "100%", md: "auto" }, minWidth: 0 }}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              mb={1}
                              display="block"
                            >
                              Personal Photo
                            </MDTypography>
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              sx={{ width: "100%", minWidth: 0 }}
                            >
                              <Avatar
                                src={personalPhotoPreview}
                                alt="Personal Photo"
                                sx={{
                                  width: { xs: 120, sm: 150 },
                                  height: { xs: 120, sm: 150 },
                                  mb: 2,
                                }}
                              >
                                {!personalPhotoPreview && <Icon>person</Icon>}
                              </Avatar>
                              <Button
                                variant="contained"
                                component="label"
                                size="small"
                                startIcon={<Icon>photo_camera</Icon>}
                                fullWidth
                                sx={{
                                  width: { xs: "100%", sm: "auto" },
                                  backgroundColor: "info.main",
                                  color: "white !important",
                                  "& .MuiSvgIcon-root": {
                                    color: "white !important",
                                  },
                                  "&:hover": {
                                    backgroundColor: "info.dark",
                                    color: "white !important",
                                  },
                                }}
                              >
                                {personalPhotoPreview ? "Change Photo" : "Upload Photo"}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={handlePersonalPhotoChange}
                                />
                              </Button>
                              {personalPhotoFile && (
                                <MDTypography variant="caption" color="text.secondary" mt={1}>
                                  {personalPhotoFile.name}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <MDBox mb={2} sx={{ width: { xs: "100%", md: "auto" }, minWidth: 0 }}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              mb={1}
                              display="block"
                            >
                              ID Card (PDF)
                            </MDTypography>
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              sx={{ width: "100%", minWidth: 0 }}
                            >
                              {idCardPreview ? (
                                <MDBox mb={2}>
                                  <Chip
                                    icon={<Icon>description</Icon>}
                                    label="ID Card Uploaded"
                                    color="success"
                                    size="medium"
                                  />
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    mt={1}
                                    display="block"
                                  >
                                    <a
                                      href={idCardPreview}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View Current ID Card
                                    </a>
                                  </MDTypography>
                                </MDBox>
                              ) : (
                                <MDBox mb={2}>
                                  <Chip label="No ID Card" color="default" size="medium" />
                                </MDBox>
                              )}
                              <Button
                                variant="contained"
                                component="label"
                                size="small"
                                startIcon={<Icon>upload_file</Icon>}
                                fullWidth
                                sx={{
                                  width: { xs: "100%", sm: "auto" },
                                  backgroundColor: "info.main",
                                  color: "white !important",
                                  "& .MuiSvgIcon-root": {
                                    color: "white !important",
                                  },
                                  "&:hover": {
                                    backgroundColor: "info.dark",
                                    color: "white !important",
                                  },
                                }}
                              >
                                {idCardPreview ? "Change ID Card" : "Upload ID Card"}
                                <input
                                  type="file"
                                  hidden
                                  accept="application/pdf"
                                  onChange={handleIdCardChange}
                                />
                              </Button>
                              {idCardFile && (
                                <MDTypography variant="caption" color="text.secondary" mt={1}>
                                  {idCardFile.name}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                        </Grid>
                      </>
                    )}

                    {/* Photo Section - Staff User */}
                    {!isPropertyOwner && (
                      <>
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2} sx={{ width: { xs: "100%", md: "auto" }, minWidth: 0 }}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              mb={1}
                              display="block"
                            >
                              Photo (Mandatory) *
                            </MDTypography>
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              sx={{ width: "100%", minWidth: 0 }}
                            >
                              <Avatar
                                src={photoPreview}
                                alt="Profile Photo"
                                sx={{
                                  width: { xs: 120, sm: 150 },
                                  height: { xs: 120, sm: 150 },
                                  mb: 2,
                                }}
                              />
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                startIcon={<Icon>photo_camera</Icon>}
                                fullWidth
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                              >
                                Change Photo
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={handlePhotoChange}
                                />
                              </Button>
                              {photoFile && (
                                <MDTypography variant="caption" color="text.secondary" mt={1}>
                                  {photoFile.name}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                        </Grid>

                        {/* ID Photo Section */}
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2} sx={{ width: { xs: "100%", md: "auto" }, minWidth: 0 }}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              mb={1}
                              display="block"
                            >
                              ID Photo (PDF)
                            </MDTypography>
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              sx={{ width: "100%", minWidth: 0 }}
                            >
                              {idPhotoPreview ? (
                                <MDBox mb={2}>
                                  <Chip
                                    icon={<Icon>description</Icon>}
                                    label="ID Photo Uploaded"
                                    color="success"
                                    size="medium"
                                  />
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    mt={1}
                                    display="block"
                                  >
                                    <a
                                      href={idPhotoPreview}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View Current ID Photo
                                    </a>
                                  </MDTypography>
                                </MDBox>
                              ) : (
                                <MDBox mb={2}>
                                  <Chip label="No ID Photo" color="default" size="medium" />
                                </MDBox>
                              )}
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                startIcon={<Icon>upload_file</Icon>}
                                fullWidth
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                              >
                                {idPhotoPreview ? "Change ID Photo" : "Upload ID Photo"}
                                <input
                                  type="file"
                                  hidden
                                  accept="application/pdf"
                                  onChange={handleIdPhotoChange}
                                />
                              </Button>
                              {idPhotoFile && (
                                <MDTypography variant="caption" color="text.secondary" mt={1}>
                                  {idPhotoFile.name}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                        </Grid>
                      </>
                    )}

                    {/* Basic Information */}
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Full Name"
                        name="fullName"
                        value={profileData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Email (Non-changeable)"
                        name="email"
                        value={profileData.email}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>lock</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {!isPropertyOwner && (
                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="ID Number"
                          name="idNumber"
                          value={profileData.idNumber}
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon>badge</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Gender *</InputLabel>
                        <Select
                          name="gender"
                          value={profileData.gender}
                          onChange={handleChange}
                          label="Gender *"
                          required
                          sx={{
                            height: "40px",
                            "& .MuiSelect-select": {
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              paddingTop: "5px",
                              paddingBottom: "5px",
                            },
                          }}
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>phone</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* Property Owner Specific Fields */}
                    {isPropertyOwner && (
                      <>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Job Type</InputLabel>
                            <Select
                              name="jobType"
                              value={profileData.jobType || ""}
                              onChange={handleChange}
                              label="Job Type"
                              sx={{
                                height: "40px",
                                "& .MuiSelect-select": {
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  paddingTop: "5px",
                                  paddingBottom: "5px",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              <MenuItem value="Private Sector">Private Sector</MenuItem>
                              <MenuItem value="Government Sector">Government Sector</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Nationality</InputLabel>
                            <Select
                              name="nationality"
                              value={profileData.nationality || ""}
                              onChange={handleChange}
                              label="Nationality"
                              sx={{
                                height: "40px",
                                "& .MuiSelect-select": {
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  paddingTop: "5px",
                                  paddingBottom: "5px",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              <MenuItem value="Egyptian">Egyptian</MenuItem>
                              <MenuItem value="Saudi Arabian">Saudi Arabian</MenuItem>
                              <MenuItem value="Emirati">Emirati</MenuItem>
                              <MenuItem value="Kuwaiti">Kuwaiti</MenuItem>
                              <MenuItem value="Qatari">Qatari</MenuItem>
                              <MenuItem value="Bahraini">Bahraini</MenuItem>
                              <MenuItem value="Omani">Omani</MenuItem>
                              <MenuItem value="Jordanian">Jordanian</MenuItem>
                              <MenuItem value="Lebanese">Lebanese</MenuItem>
                              <MenuItem value="Syrian">Syrian</MenuItem>
                              <MenuItem value="Iraqi">Iraqi</MenuItem>
                              <MenuItem value="Yemeni">Yemeni</MenuItem>
                              <MenuItem value="Palestinian">Palestinian</MenuItem>
                              <MenuItem value="Tunisian">Tunisian</MenuItem>
                              <MenuItem value="Algerian">Algerian</MenuItem>
                              <MenuItem value="Moroccan">Moroccan</MenuItem>
                              <MenuItem value="Libyan">Libyan</MenuItem>
                              <MenuItem value="Sudanese">Sudanese</MenuItem>
                              <MenuItem value="American">American</MenuItem>
                              <MenuItem value="British">British</MenuItem>
                              <MenuItem value="Canadian">Canadian</MenuItem>
                              <MenuItem value="Australian">Australian</MenuItem>
                              <MenuItem value="French">French</MenuItem>
                              <MenuItem value="German">German</MenuItem>
                              <MenuItem value="Italian">Italian</MenuItem>
                              <MenuItem value="Spanish">Spanish</MenuItem>
                              <MenuItem value="Turkish">Turkish</MenuItem>
                              <MenuItem value="Indian">Indian</MenuItem>
                              <MenuItem value="Pakistani">Pakistani</MenuItem>
                              <MenuItem value="Bangladeshi">Bangladeshi</MenuItem>
                              <MenuItem value="Filipino">Filipino</MenuItem>
                              <MenuItem value="Indonesian">Indonesian</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <MDInput
                            fullWidth
                            label="Address"
                            name="address"
                            value={profileData.address || ""}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>home</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        {/* Bank Details Section */}
                        <Grid item xs={12}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2} mt={2}>
                            Bank Details
                          </MDTypography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="Bank Name *"
                            name="bankBankName"
                            value={profileData.bankDetails?.bankName || ""}
                            onChange={handleChange}
                            error={!!bankErrors.bankName}
                            helperText={bankErrors.bankName}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>account_balance</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="Account Holder Name *"
                            name="bankAccountHolderName"
                            value={profileData.bankDetails?.accountHolderName || ""}
                            onChange={handleChange}
                            error={!!bankErrors.accountHolderName}
                            helperText={bankErrors.accountHolderName}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>person</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="Account Number *"
                            name="bankAccountNumber"
                            value={profileData.bankDetails?.accountNumber || ""}
                            onChange={handleChange}
                            error={!!bankErrors.accountNumber}
                            helperText={bankErrors.accountNumber}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>credit_card</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="IBAN (Optional)"
                            name="bankIban"
                            value={profileData.bankDetails?.iban || ""}
                            onChange={handleChange}
                            error={!!bankErrors.iban}
                            helperText={bankErrors.iban || "International Bank Account Number"}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>account_balance_wallet</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="SWIFT Code (Optional)"
                            name="bankSwiftCode"
                            value={profileData.bankDetails?.swiftCode || ""}
                            onChange={handleChange}
                            error={!!bankErrors.swiftCode}
                            helperText={bankErrors.swiftCode || "8-11 alphanumeric characters"}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>code</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="Branch Name"
                            name="bankBranchName"
                            value={profileData.bankDetails?.branchName || ""}
                            onChange={handleChange}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>location_on</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Staff User Specific Fields */}
                    {!isPropertyOwner && (
                      <>
                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="Company Number *"
                            name="companyNumber"
                            value={profileData.companyNumber}
                            onChange={handleChange}
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>business</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="Relevant Phone Number 1"
                            name="relevantPhone0"
                            value={profileData.relevantPhoneNumbers[0] || ""}
                            onChange={handleChange}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>phone</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            label="Relevant Phone Number 2"
                            name="relevantPhone1"
                            value={profileData.relevantPhoneNumbers[1] || ""}
                            onChange={handleChange}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>phone</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <MDInput
                            fullWidth
                            label="Address"
                            name="address"
                            value={profileData.address}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>location_on</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Marital Status</InputLabel>
                            <Select
                              name="maritalStatus"
                              value={profileData.maritalStatus}
                              onChange={handleChange}
                              label="Marital Status"
                              sx={{
                                height: "40px",
                                "& .MuiSelect-select": {
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  paddingTop: "5px",
                                  paddingBottom: "5px",
                                },
                              }}
                            >
                              <MenuItem value="Single">Single</MenuItem>
                              <MenuItem value="Married">Married</MenuItem>
                              <MenuItem value="Divorced">Divorced</MenuItem>
                              <MenuItem value="Widowed">Widowed</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            type="number"
                            label="Family Members Number"
                            name="familyMembersNumber"
                            value={profileData.familyMembersNumber}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>people</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MDInput
                            fullWidth
                            type="email"
                            label="Personal Email"
                            name="personalEmail"
                            value={profileData.personalEmail}
                            onChange={handleChange}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>email</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Nationality</InputLabel>
                            <Select
                              name="nationality"
                              value={profileData.nationality || ""}
                              onChange={handleChange}
                              label="Nationality"
                              sx={{
                                height: "40px",
                                "& .MuiSelect-select": {
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  paddingTop: "5px",
                                  paddingBottom: "5px",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              <MenuItem value="Egyptian">Egyptian</MenuItem>
                              <MenuItem value="Saudi Arabian">Saudi Arabian</MenuItem>
                              <MenuItem value="Emirati">Emirati</MenuItem>
                              <MenuItem value="Kuwaiti">Kuwaiti</MenuItem>
                              <MenuItem value="Qatari">Qatari</MenuItem>
                              <MenuItem value="Bahraini">Bahraini</MenuItem>
                              <MenuItem value="Omani">Omani</MenuItem>
                              <MenuItem value="Jordanian">Jordanian</MenuItem>
                              <MenuItem value="Lebanese">Lebanese</MenuItem>
                              <MenuItem value="Syrian">Syrian</MenuItem>
                              <MenuItem value="Iraqi">Iraqi</MenuItem>
                              <MenuItem value="Yemeni">Yemeni</MenuItem>
                              <MenuItem value="Palestinian">Palestinian</MenuItem>
                              <MenuItem value="Tunisian">Tunisian</MenuItem>
                              <MenuItem value="Algerian">Algerian</MenuItem>
                              <MenuItem value="Moroccan">Moroccan</MenuItem>
                              <MenuItem value="Libyan">Libyan</MenuItem>
                              <MenuItem value="Sudanese">Sudanese</MenuItem>
                              <MenuItem value="American">American</MenuItem>
                              <MenuItem value="British">British</MenuItem>
                              <MenuItem value="Canadian">Canadian</MenuItem>
                              <MenuItem value="Australian">Australian</MenuItem>
                              <MenuItem value="French">French</MenuItem>
                              <MenuItem value="German">German</MenuItem>
                              <MenuItem value="Italian">Italian</MenuItem>
                              <MenuItem value="Spanish">Spanish</MenuItem>
                              <MenuItem value="Turkish">Turkish</MenuItem>
                              <MenuItem value="Indian">Indian</MenuItem>
                              <MenuItem value="Pakistani">Pakistani</MenuItem>
                              <MenuItem value="Bangladeshi">Bangladeshi</MenuItem>
                              <MenuItem value="Filipino">Filipino</MenuItem>
                              <MenuItem value="Indonesian">Indonesian</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <MDBox display="flex" justifyContent="flex-end" gap={2}>
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          onClick={() => fetchProfile()}
                          disabled={submitting}
                        >
                          Cancel
                        </MDButton>
                        <MDButton
                          variant="gradient"
                          color="info"
                          type="submit"
                          disabled={submitting}
                          startIcon={
                            submitting ? <CircularProgress size={20} /> : <Icon>save</Icon>
                          }
                        >
                          {submitting ? "Saving..." : "Save Changes"}
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>

          {/* Change Password */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Change Password
                </MDTypography>
                {passwordMessage.text && (
                  <MDBox mb={2}>
                    <Alert severity={passwordMessage.type}>{passwordMessage.text}</Alert>
                  </MDBox>
                )}
                <form onSubmit={handleChangePassword}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        fullWidth
                        type="password"
                        label="Current Password"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordFormChange}
                        required
                        autoComplete="current-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>lock</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        fullWidth
                        type="password"
                        label="New Password"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordFormChange}
                        required
                        autoComplete="new-password"
                        helperText="At least 6 characters"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>lock_outline</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        fullWidth
                        type="password"
                        label="Confirm New Password"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordFormChange}
                        required
                        autoComplete="new-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>lock_outline</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDButton
                        type="submit"
                        variant="gradient"
                        color="dark"
                        disabled={passwordSubmitting}
                        startIcon={
                          passwordSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Icon>key</Icon>
                          )
                        }
                      >
                        {passwordSubmitting ? "Updating..." : "Change Password"}
                      </MDButton>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Profile;
